import { FC, ReactNode, useId } from 'react';

import { Badge, Box, Expandable, RadioCard, Stack, Text } from '@eneco/sparky';

import { DeliveryDateOption } from '../utils/deliveryDateOptionSchema_B';

/** The panel is driven by selection, so it never asks to change its own state. */
const noop = () => undefined;

interface DeliveryDateOptionCardProps {
  option: DeliveryDateOption;
  title: string;
  /**
   * Rendered under the title and included in the radio's accessible name. A node rather than a
   * string so a caller can emphasise part of it — the `ASAP` card bolds its start date.
   */
  subtitle: ReactNode;
  badge?: string;
  isSelected: boolean;
  /** Rendered only while the card is selected. */
  children?: ReactNode;
}

export const DeliveryDateOptionCard: FC<DeliveryDateOptionCardProps> = ({
  option,
  title,
  subtitle,
  badge,
  isSelected,
  children,
}) => {
  const uid = useId();
  const titleId = `${uid}-title`;
  const subtitleId = `${uid}-subtitle`;

  return (
    <RadioCard
      value={option}
      ariaLabelledby={`${titleId} ${subtitleId}`}
      overflow="visible"
      badge={
        badge ? (
          <Badge size="S" color="brandDefault">
            {badge}
          </Badge>
        ) : undefined
      }>
      <Box padding="4">
        <Stack>
          <Stack direction="row" gap="2" alignX="justify" alignY="center">
            <Stack.Item grow>
              <Stack gap="1">
                <Text size="BodyS" weight="bold" id={titleId}>
                  {title}
                </Text>
                <Text size="BodyS" color="textLowEmphasis" id={subtitleId}>
                  {subtitle}
                </Text>
              </Stack>
            </Stack.Item>

            <Stack.Item shrink={false}>
              <RadioCard.Indicator />
            </Stack.Item>
          </Stack>

          <Expandable isOpen={isSelected} setOpen={noop}>
            <Expandable.Content hasVisibleOverflow>{children}</Expandable.Content>
          </Expandable>
        </Stack>
      </Box>
    </RadioCard>
  );
};
